import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, Heart, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  const categories = [
    { name: 'Men', href: '/shop/men' },
    { name: 'Women', href: '/shop/women' },
    { name: 'Kids', href: '/shop/kids' },
    { name: 'Accessories', href: '/shop/accessories' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="font-['Syne'] text-xl md:text-2xl font-bold tracking-tight">
              LOSTNFOUND<span className="text-[#FF3B30]">860</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <DropdownMenu>
              <DropdownMenuTrigger className="uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors" data-testid="shop-dropdown">
                Shop
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-neutral-900 border-neutral-800">
                {categories.map((cat) => (
                  <DropdownMenuItem key={cat.name} asChild>
                    <Link 
                      to={cat.href} 
                      className="uppercase text-xs tracking-widest"
                      data-testid={`nav-${cat.name.toLowerCase()}`}
                    >
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/shop" className="uppercase text-xs tracking-widest" data-testid="nav-all">
                    View All
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/drops" 
              className="uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors"
              data-testid="nav-drops"
            >
              Drops
            </Link>
            <Link 
              to="/lookbook" 
              className="uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors"
              data-testid="nav-lookbook"
            >
              Lookbook
            </Link>
            <Link 
              to="/about" 
              className="uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors"
              data-testid="nav-about"
            >
              About
            </Link>
          </div>

          {/* Right icons */}
          <div className="flex items-center space-x-4">
            <button className="hidden md:block p-2 hover:text-[#FF3B30] transition-colors" data-testid="search-btn">
              <Search size={20} />
            </button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="p-2 hover:text-[#FF3B30] transition-colors" data-testid="user-menu">
                  <User size={20} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-neutral-900 border-neutral-800">
                  <DropdownMenuItem className="text-muted-foreground text-xs">
                    Hi, {user?.first_name}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" data-testid="nav-dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=orders" data-testid="nav-orders">My Orders</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=wishlist" data-testid="nav-wishlist">Wishlist</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" className="p-2 hover:text-[#FF3B30] transition-colors" data-testid="login-link">
                <User size={20} />
              </Link>
            )}

            {isAuthenticated && (
              <Link to="/dashboard?tab=wishlist" className="hidden md:block p-2 hover:text-[#FF3B30] transition-colors" data-testid="wishlist-link">
                <Heart size={20} />
              </Link>
            )}

            <Link to="/cart" className="p-2 hover:text-[#FF3B30] transition-colors relative" data-testid="cart-link">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-xs w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-neutral-800 animate-slideUp">
            <div className="flex flex-col space-y-4">
              <span className="uppercase text-xs tracking-widest text-muted-foreground">Shop</span>
              {categories.map((cat) => (
                <Link
                  key={cat.name}
                  to={cat.href}
                  className="uppercase text-sm tracking-widest pl-4"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${cat.name.toLowerCase()}`}
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/shop"
                className="uppercase text-sm tracking-widest pl-4"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-all"
              >
                View All
              </Link>
              <div className="border-t border-neutral-800 pt-4 mt-4">
                <Link
                  to="/drops"
                  className="block uppercase text-sm tracking-widest"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Drops
                </Link>
              </div>
              <Link
                to="/lookbook"
                className="uppercase text-sm tracking-widest"
                onClick={() => setMobileMenuOpen(false)}
              >
                Lookbook
              </Link>
              <Link
                to="/about"
                className="uppercase text-sm tracking-widest"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/size-guide"
                className="uppercase text-sm tracking-widest"
                onClick={() => setMobileMenuOpen(false)}
              >
                Size Guide
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
